import type {
  PlanTablePositionAnchor,
  PlanTableSorting,
  PlanTapelineConfig,
  PlanTableTemplateOptions,
  PlanTableType,
  TableData,
} from 'src/domain/planTables/types'
import { deleteRequest, patchRequest, postRequest } from '../utils'

interface PlanTableCreateBaseDTO {
  id?: string
  name: string
  position: { x: number; y: number; z: number }
  positionAnchor: PlanTablePositionAnchor
  filterViewId: string
  isVisible: boolean
  isActive: boolean
  planConfig: PlanTapelineConfig
  sorting?: PlanTableSorting | null
}

export interface CustomPlanTableCreateDTO extends PlanTableCreateBaseDTO {
  tableType: 'CustomPlanTable'
  table: TableData
}

export interface TemplatePlanTableCreateDTO extends PlanTableCreateBaseDTO {
  tableType: 'TemplatePlanTable'
  templateKey: string
  templateOptions?: PlanTableTemplateOptions
}

export type PlanTableCreateDTO = CustomPlanTableCreateDTO | TemplatePlanTableCreateDTO

export interface CustomPlanTableModifyDTO extends PlanTableCreateBaseDTO {
  tableType: 'CustomPlanTable'
  table: TableData
}

export interface TemplatePlanTableModifyDTO extends PlanTableCreateBaseDTO {
  tableType: 'TemplatePlanTable'
  templateKey: string
  templateOptions?: PlanTableTemplateOptions
}

export type PlanTableModifyDTO = CustomPlanTableModifyDTO | TemplatePlanTableModifyDTO

export interface PlanTableDTO {
  id: string
  name: string
  position: { x: number; y: number; z: number }
  positionAnchor: PlanTablePositionAnchor
  filterViewId: string
  tableType: PlanTableType
  templateKey?: string | null
  table: TableData
  isVisible: boolean
  isActive: boolean
  planConfig: PlanTapelineConfig
  sorting?: PlanTableSorting | null
  templateOptions?: PlanTableTemplateOptions
  dateCreated: string
  dateModified: string
}

export const createPlanTables: RequestTypeVariable = {
  getKey: (projectGuid: string) => ['createPlanTables', projectGuid],
  request: async (
    projectGuid: string,
    planTables: PlanTableCreateDTO[],
  ): Promise<PlanTableDTO[]> => {
    const { data: result } = await postRequest<PlanTableDTO[]>({
      url: `/projects/${projectGuid}/plan-tables`,
      data: planTables,
    })
    return result || []
  },
}

export const updatePlanTable: RequestTypeVariable = {
  getKey: (projectGuid: string, planTableId: string) => [
    'updatePlanTable',
    projectGuid,
    planTableId,
  ],
  request: async (
    projectGuid: string,
    planTableId: string,
    data: PlanTableModifyDTO,
  ): Promise<PlanTableDTO[]> => {
    const { data: result } = await patchRequest<PlanTableDTO[]>({
      url: `/projects/${projectGuid}/plan-tables/${planTableId}`,
      data,
    })
    return result || []
  },
}

export const deletePlanTables: RequestTypeVariable = {
  getKey: (projectGuid: string) => ['deletePlanTables', projectGuid],
  request: async (projectGuid: string, planTableIds: string[]): Promise<PlanTableDTO[]> => {
    const { data: result } = await deleteRequest<PlanTableDTO[]>({
      url: `/projects/${projectGuid}/plan-tables`,
      data: planTableIds,
    })
    return result || []
  },
}
